import { ACTIVE_TASK_STATUSES, type TaskStatus } from './domain.js'

const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

/** A period during which the clock was stopped by a manager-flagged external delay. */
export interface DelayWindow {
  readonly startsAt: Date
  readonly endsAt: Date
}

/**
 * Collapse overlapping and touching windows so their durations can be summed without
 * double counting. Two managers flagging overlapping delays on the same task must not
 * buy the employee the same hours twice.
 *
 * Inverted windows (endsAt before startsAt) are dropped rather than contributing
 * negative time.
 */
export function mergeDelayWindows(windows: readonly DelayWindow[]): DelayWindow[] {
  const valid = windows
    .filter((x) => x.endsAt.getTime() > x.startsAt.getTime())
    .map((x) => ({ startsAt: x.startsAt, endsAt: x.endsAt }))
    .sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime())

  const merged: DelayWindow[] = []
  for (const window of valid) {
    const last = merged[merged.length - 1]
    if (last !== undefined && window.startsAt.getTime() <= last.endsAt.getTime()) {
      if (window.endsAt.getTime() > last.endsAt.getTime()) {
        merged[merged.length - 1] = { startsAt: last.startsAt, endsAt: window.endsAt }
      }
      continue
    }
    merged.push(window)
  }
  return merged
}

export function totalDelayMs(windows: readonly DelayWindow[]): number {
  return mergeDelayWindows(windows).reduce(
    (sum, x) => sum + (x.endsAt.getTime() - x.startsAt.getTime()),
    0,
  )
}

/**
 * The bar the employee is actually measured against: the original deadline pushed
 * forward by external delays and approved extensions. External delay does not forgive
 * a late submission — it moves the deadline, which is what "excludes external delays"
 * means in the analytics screens.
 *
 * Clamped so it can only ever move forward.
 */
export function effectiveDeadline(
  deadlineAt: Date,
  windows: readonly DelayWindow[],
  extensionMs = 0,
): Date {
  const added = totalDelayMs(windows) + Math.max(0, extensionMs)
  return new Date(deadlineAt.getTime() + added)
}

/**
 * Overdue is derived, never stored. A task is overdue only while the clock is running:
 * SUBMITTED is waiting on the manager and BLOCKED is waiting on someone else, so
 * neither counts against the employee.
 *
 * Exactly on the deadline is not overdue.
 */
export function isOverdue(
  effectiveDeadlineAt: Date,
  status: TaskStatus,
  now: Date,
): boolean {
  if (!ACTIVE_TASK_STATUSES.includes(status)) return false
  return effectiveDeadlineAt.getTime() < now.getTime()
}

export interface Remaining {
  readonly days: number
  readonly hours: number
  readonly minutes: number
  /**
   * Optional on purpose. Existing tests construct Remaining as object literals without it,
   * and a required field would break them. Populated by remaining().
   */
  readonly seconds?: number
  readonly expired: boolean
}

function withSeconds(r: Omit<Remaining, 'seconds'>, seconds: number): Remaining {
  return Object.defineProperty(r, 'seconds', {
    value: seconds,
    enumerable: false,
    writable: false,
    configurable: false,
  }) as Remaining
}

/** Time left, floored to whole minutes for the existing summaries, plus seconds for live countdowns. */
export function remaining(effectiveDeadlineAt: Date, now: Date): Remaining {
  const ms = effectiveDeadlineAt.getTime() - now.getTime()
  if (ms <= 0) return withSeconds({ days: 0, hours: 0, minutes: 0, expired: true }, 0)
  return withSeconds(
    {
    days: Math.floor(ms / DAY_MS),
    hours: Math.floor((ms % DAY_MS) / HOUR_MS),
    minutes: Math.floor((ms % HOUR_MS) / MINUTE_MS),
    expired: false,
    },
    Math.floor((ms % MINUTE_MS) / 1000),
  )
}

const pad = (n: number) => String(n).padStart(2, '0')

/** `01d 04h`, matching the DUE column and the employee card. Em dash when expired. */
export function formatRemaining(r: Remaining): string {
  if (r.expired) return '—'
  return `${pad(r.days)}d ${pad(r.hours)}h`
}

/**
 * The live clock. Separate from formatRemaining, which stays `01d 04h` because the API's
 * dueIn and the manager DUE column are built on it.
 */
export function formatCountdown(r: Remaining): string {
  if (r.expired) return '00:00:00'
  const clock = `${pad(r.hours)}:${pad(r.minutes)}:${pad(r.seconds ?? 0)}`
  return r.days > 0 ? `${pad(r.days)}d ${clock}` : clock
}

export type DeadlinePressure = 'calm' | 'warning' | 'urgent'

/**
 * How far through its allocated window a task is, bucketed for display. Measured on
 * elapsed share of assignedAt -> effectiveDeadlineAt, so a two-hour task and a two-week
 * task turn amber at the same *proportion* rather than the same absolute time left.
 *
 * Lives here, not in a component: the employee card and the manager list must agree, and
 * two implementations would disagree at a boundary.
 */
export function deadlinePressure(
  assignedAt: Date,
  effectiveDeadlineAt: Date,
  now: Date,
): DeadlinePressure {
  const span = effectiveDeadlineAt.getTime() - assignedAt.getTime()
  // A zero or inverted window has no "half way" — it is already out of time.
  if (span <= 0) return 'urgent'
  const elapsed = (now.getTime() - assignedAt.getTime()) / span
  if (elapsed >= 0.75) return 'urgent'
  if (elapsed >= 0.5) return 'warning'
  return 'calm'
}
